import { memo } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { CHAT_BG_PATTERN, URL_BUTTON_COLOR } from "../constants";
import { renderBodyRich, renderHeaderText } from "../utils/messageTemplatePreviewRichText";

export interface MessageTemplatePhonePreviewContentProps {
  isCard: boolean;
  header: string;
  body: string;
  hasButton: boolean;
  buttonText: string;
  buttonType: string;
  footerTime: string;
}

const MessageTemplatePhonePreviewContent = memo(
  ({
    isCard,
    header,
    body,
    hasButton,
    buttonText,
    buttonType,
    footerTime,
  }: MessageTemplatePhonePreviewContentProps) => {
    const hasMessageText = Boolean(header.trim() || body.trim());
    const showButtonRow = Boolean(hasButton && buttonText.trim());
    const hasContent = Boolean(hasMessageText || showButtonRow);

    return (
      <Box
        sx={{
          p: isCard ? 0.5 : 1.5,
          minHeight: isCard ? 100 : 220,
          maxHeight: isCard ? 200 : 360,
          overflowY: "auto",
          backgroundColor: isCard ? "transparent" : "#efeae2",
          backgroundImage: isCard ? "none" : CHAT_BG_PATTERN
        }}
      >
        {!hasContent && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              py: isCard ? 1.5 : 3,
              px: isCard ? 0.5 : 0,
              textAlign: "center",
              fontSize: isCard ? "0.75rem" : undefined,
            }}
          >
            {isCard
              ? "Sem conteúdo para exibir."
              : "Preencha cabeçalho ou corpo para ver a prévia."}
          </Typography>
        )}

        {hasContent && (

          <Box
            sx={{
              maxWidth: "100%",
              width: "100%",
              position: "relative",
              filter: "drop-shadow(0 1px 0.5px rgba(0,0,0,0.13))",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                left: -6,
                top: 1.5,
                width: 0,
                height: 0,
                borderStyle: "solid",
                borderWidth: "0 8px 8px 0",
                borderColor: isCard ? "transparent #efeae2 transparent transparent" : "transparent #fff  transparent transparent",
                zIndex: 0,
              }}
            />

            <Box
              sx={{
                position: "relative",
                zIndex: 1,
                backgroundColor: isCard ? "#f1f1f1" : "#fff",
                borderRadius: "4px 8px 8px 8px",
                overflow: "hidden",
                border: isCard ? "1px solid #efeae2" : "1px solid rgba(0,0,0,0.06)",
              }}
            >
              {(hasMessageText || showButtonRow) && (
                <>
                  {hasMessageText ? (
                    <Box
                      sx={{
                        px: isCard ? 1 : 1.35,
                        pt: isCard ? 0.75 : 1.1,
                        pb: isCard ? 0.5 : 0.75,
                      }}
                    >
                      {header.trim() && (
                        <Typography
                          component="div"
                          variant="body2"
                          fontWeight={700}
                          sx={{
                            mb: body.trim() ? 0.6 : 0,
                            lineHeight: 1.35,
                            fontSize: isCard ? "0.75rem" : undefined,
                            color: "rgba(0,0,0,0.87)",
                          }}
                        >
                          {renderHeaderText(header.trim())}
                        </Typography>
                      )}
                      {body.trim() && (
                        <Typography
                          component="div"
                          variant="body2"
                          sx={{
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            lineHeight: 1.45,
                            fontSize: isCard ? "0.72rem" : "0.8125rem",
                            color: "rgba(0,0,0,0.87)",
                            display: isCard ? "-webkit-box" : undefined,
                            WebkitLineClamp: isCard ? 4 : undefined,
                            WebkitBoxOrient: isCard ? "vertical" : undefined,
                            overflow: isCard ? "hidden" : undefined,
                          }}
                        >
                          {renderBodyRich(body.trim())}
                        </Typography>
                      )}

                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-end"
                        sx={{ mt: isCard ? 0.5 : 0.75 }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: "rgba(0,0,0,0.45)",
                            fontSize: isCard ? "0.6rem" : "0.68rem",
                          }}
                        >
                          Fluxmed
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "rgba(0,0,0,0.45)",
                            fontSize: isCard ? "0.6rem" : "0.68rem",
                          }}
                        >
                          {footerTime}
                        </Typography>
                      </Stack>
                    </Box>
                  ) : null}

                  {showButtonRow ? (
                    <>
                      {hasMessageText ? (
                        <Box
                          sx={{
                            borderTop: "1px solid rgba(0,0,0,0.08)",
                          }}
                        />
                      ) : null}
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="center"
                        spacing={0.75}
                        sx={{
                          py: isCard ? 0.75 : 1.15,
                          px: 1,
                          cursor: "default",
                          userSelect: "none",
                        }}
                      >
                        {buttonType === "URL" && (
                          <OpenInNewIcon
                            sx={{
                              fontSize: isCard ? 15 : 18,
                              color: URL_BUTTON_COLOR,
                            }}
                          />
                        )}
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{
                            color: URL_BUTTON_COLOR,
                            fontWeight: 600,
                            fontSize: isCard ? "0.72rem" : "0.8125rem",
                          }}
                        >
                          {buttonText.trim()
                            ? renderHeaderText(buttonText.trim())
                            : "Abrir link"}
                        </Typography>
                      </Stack>
                    </>
                  ) : null}
                </>
              )}
            </Box>
          </Box>

        )}
      </Box>
    );
  },
);

MessageTemplatePhonePreviewContent.displayName = "MessageTemplatePhonePreviewContent";

export default MessageTemplatePhonePreviewContent;
